import React from 'react';
import Table, { TableColumn } from '../../components/common/Table';
import { CourseDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { getBadgeColors } from '../../utils/displayUtils';
import Badge from '../common/Badge';
import FileIcons from '../common/FileIcons';
import createCourseActions from '../actionMenus/CourseActions';
import MemberNoteIcons from '../common/MemberNoteIcons';
import { getRoles } from '../../utils/tokenUtils';
import ReviewPanel from '../reviewPanels/ReviewPanel';

interface CourseTableProps {
  data: CourseDTO[];
  handleViewMemberDetails: (row: CourseDTO) => void;
  handleViewEvaluation: (row: CourseDTO) => void;
  handleEdit: (row: CourseDTO) => void;
  handleDelete: (row: CourseDTO) => Promise<void>;
  handleSendEmail: (row: CourseDTO) => void;
  handleViewAllEmails: (row: CourseDTO) => void;
  handleViewRecordEmails: (row: CourseDTO) => void;
  handleRestore: (row: CourseDTO) => Promise<void>;
  handleApprove: (row: CourseDTO) => void;
  handleShipped: (row: CourseDTO) => void;
  handleChangeStatus: (row: CourseDTO) => void;
  handleImpersonateMemberLogin: (row: CourseDTO) => void;
  handleAddNote: (row: CourseDTO) => void;
  handleViewNoteHistory: (row: CourseDTO) => void;
  handleViewInvoiceHistory: (row: CourseDTO) => void;
  handleSendProofOfPaymentLink: (row: CourseDTO) => void;
  handleCompleteEftPayment: (row: CourseDTO) => void;
  handleDownloadForfeitureDocument?: (row: CourseDTO) => void;
  handleDownloadOriginalCertificate?: (row: CourseDTO) => void;
  handleMemberClick?: (memberId: string) => void;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
  sortBy?: string;
  sortDescending?: boolean;
  onSort?: (sortKey: string) => void;
  selectedIds?: Set<string>;
  onSelectRow?: (id: string, isSelected: boolean) => void;
  onSelectAll?: (isSelected: boolean) => void;
}

const CourseTable: React.FC<CourseTableProps> = ({
  data,
  handleViewMemberDetails,
  handleViewEvaluation,
  handleEdit,
  handleDelete,
  handleSendEmail,
  handleViewAllEmails,
  handleViewRecordEmails,
  handleRestore,
  handleApprove,
  handleShipped,
  handleChangeStatus,
  handleImpersonateMemberLogin,
  handleAddNote,
  handleViewNoteHistory,
  handleViewInvoiceHistory,
  handleSendProofOfPaymentLink,
  handleCompleteEftPayment,
  handleDownloadForfeitureDocument,
  handleDownloadOriginalCertificate,
  handleMemberClick,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
  sortBy,
  sortDescending,
  onSort,
  selectedIds = new Set(),
  onSelectRow,
  onSelectAll,
}) => {
  const roles = getRoles();
  const isAdmin = roles?.includes('Admin');
  const allCurrentPageIds = data.map(row => row.id).filter((id): id is string => !!id);
  const allSelected = allCurrentPageIds.length > 0 && allCurrentPageIds.every(id => selectedIds.has(id));
  const someSelected = allCurrentPageIds.some(id => selectedIds.has(id)) && !allSelected;

  const columns: TableColumn[] = [
    ...(onSelectRow && onSelectAll
      ? [
        {
          label: (
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = someSelected;
              }}
              onChange={(e) => onSelectAll?.(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 dark:border-gray-600"
            />
          ),
          key: 'checkbox',
          render: (row: CourseDTO) => (
            <input
              type="checkbox"
              checked={selectedIds.has(row.id ?? '')}
              onChange={(e) => onSelectRow?.(row.id ?? '', e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              className="w-4 h-4 rounded border-gray-300 dark:border-gray-600"
            />
          ),
        },
      ]
      : []),
    {
      label: 'Member',
      key: 'member',
      sortable: true,
      sortKey: 'FirstName',
      showInReview: true,
      render: (row: CourseDTO) => (
        <div
          onClick={() => handleMemberClick?.(row.member?.id ?? '')}
          className="flex flex-col gap-1 cursor-pointer"
        >
          <span>
            {row.member?.firstName} {row.member?.lastName}
          </span>
          <span>{row.member?.idPassportNumber ?? ''}</span>
          <MemberNoteIcons member={row.member} tableContext="courses" recordId={row.id} />
        </div>
      ),
    },
    {
      label: 'Membership',
      key: 'membershipType',
      sortable: true,
      sortKey: 'MemberTypeTitle',
      showInReview: true,
      render: (row: CourseDTO) => <span>{row.member?.memberType?.title}</span>,
    },
    {
      label: 'FCA',
      key: 'memberType',
      sortable: true,
      sortKey: 'MemberCategoryTitle',
      showInReview: true,
      render: (row: CourseDTO) => <span>{row.member?.memberCategory?.title}</span>,
    },

    {
      label: 'Course',
      key: 'courseTypeId',
      sortable: true,
      sortKey: 'CourseTypeTitle',
      showInReview: true,
      render: (row: CourseDTO) => <span>{row.courseType?.title}</span>,
    },

    {
      label: 'Material',
      key: 'courseMaterialTypeId',
      sortable: true,
      sortKey: 'CourseMaterialTypeTitle',
      showInReview: true,
      render: (row: CourseDTO) => <span>{row.courseMaterialType?.title}</span>,
    },

    {
      label: 'Proof',
      key: 'proofOfPaymentFileId',
      render: (row: CourseDTO) => (
        <FileIcons
          files={[
            {
              id: row.proofOfPaymentFileId,
              label: 'Proof of payment',
            },
          ]}
          renderSidePanel={isAdmin ? () => <ReviewPanel row={row} columns={columns} /> : undefined}
        />
      ),
    },

    {
      label: 'Waybill',
      key: 'courierTrackingReference',
      sortable: true,
      sortKey: 'CourierTrackingReference',
    },

    {
      label: 'Status',
      key: 'courseStatusId',
      sortable: true,
      sortKey: 'CourseStatusTitle',
      showInReview: true,
      render: (row: CourseDTO) => {
        if (row.isDeleted) {
          return <Badge label="Deleted" variant="error" />;
        }
        const base = row.courseStatus?.colourGrading?.colour ?? '#ffffffff';
        const { bg, text } = getBadgeColors(base, 0.18, 0.4);
        return base ? (
          <Badge
            label={row.courseStatus?.title || 'Unknown'}
            variant="custom"
            customBgColor={bg}
            customTextColor={text}
          />
        ) : (
          <span>{row.courseStatus?.title || 'Unknown'}</span>
        );
      },
    },

    {
      label: 'Evaluation',
      key: 'evaluationStatusId',
      sortable: true,
      sortKey: 'EvaluationStatusTitle',
      showInReview: true,
      render: (row: CourseDTO) => {
        const base = row.evaluationStatus?.colourGrading?.colour ?? '#ffffffff';
        const { bg, text } = getBadgeColors(base, 0.18, 0.4);
        return base ? (
          <Badge
            label={row.evaluationStatus?.title || 'Unknown'}
            variant="custom"
            customBgColor={bg}
            customTextColor={text}
          />
        ) : (
          <span>{row.evaluationStatus?.title || 'Unknown'}</span>
        );
      },
    },

    {
      label: 'Score',
      key: 'evaluationScorePercentage',
      sortable: true,
      sortKey: 'EvaluationScorePercentage',
      showInReview: true,
      render: (row: CourseDTO) => {
        return (
          <span>
            {row.evaluationScorePercentage && row.evaluationScoreMark
              ? `${row.evaluationScorePercentage}% / ${row.evaluationScoreMark}`
              : '-'}
          </span>
        );
      },
    },

    {
      label: 'Practical Score',
      key: 'scores',
      sortable: false,
      showInReview: true,
      render: (row: CourseDTO) => {
        const latestScore = row.scores
          ?.slice()
          .sort((a, b) => new Date(b.dateActivity || 0).getTime() - new Date(a.dateActivity || 0).getTime())[0];

        return (
          <span>
            {latestScore?.grandTotalPercentage != null && latestScore?.grandTotalMark != null
              ? `${latestScore.grandTotalPercentage.toFixed(1)}% / ${latestScore.grandTotalMark.toFixed(1)}`
              : '-'}
          </span>
        );
      },
    },

    {
      label: 'Target Files',
      key: 'scoreTargetFiles',
      render: (row: CourseDTO) => {
        const latestScore = row.scores
          ?.slice()
          .sort((a, b) => new Date(b.dateActivity || 0).getTime() - new Date(a.dateActivity || 0).getTime())[0];

        const targetFiles = latestScore?.scoreTargetFiles?.map(stf => ({
          id: stf.fileRecordId,
          label: `Target ${stf.fileRecordId}`,
        })) || [];

        return targetFiles.length > 0 ? (
          <FileIcons
            files={targetFiles}
            renderSidePanel={isAdmin ? () => <ReviewPanel row={row} columns={columns} /> : undefined}
          />
        ) : <span>-</span>;
      },
    },

    {
      label: 'Payment',
      key: 'paymentTypeId',
      sortable: true,
      sortKey: 'PaymentTypeTitle',
      showInReview: true,
      render: (row: CourseDTO) => {
        const base = row.paymentType?.colourGrading?.colour ?? '#ffffffff';
        const { bg, text } = getBadgeColors(base, 0.18, 0.4);
        return base ? (
          <Badge
            label={row.paymentType?.title || 'Outstanding'}
            variant="custom"
            customBgColor={bg}
            customTextColor={text}
          />
        ) : (
          <span>{row.paymentType?.title || 'Outstanding'}</span>
        );
      },
    },

    {
      label: 'Updated',
      key: 'updatedUtc',
      sortable: true,
      sortKey: 'UpdatedUtc',
      format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
    },

    {
      label: 'Added',
      key: 'createdUtc',
      sortable: true,
      sortKey: 'CreatedUtc',
      format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
    },
  ];

  return (
    <Table
      columns={columns}
      data={data}
      actions={{
        onEdit: handleEdit,
        extra: (row) =>
          createCourseActions({
            onViewMemberDetails: handleViewMemberDetails,
            onView: handleEdit,
            onViewEvaluation: handleViewEvaluation,
            onDelete: handleDelete,
            onSendEmail: handleSendEmail,
            onViewAllEmails: handleViewAllEmails,
            onViewRecordEmails: handleViewRecordEmails,
            onRestore: handleRestore,
            onApprove: handleApprove,
            onShipped: handleShipped,
            onChangeStatus: handleChangeStatus,
            onImpersonateMemberLogin: handleImpersonateMemberLogin,
            onAddNote: handleAddNote,
            onViewNoteHistory: handleViewNoteHistory,
            onViewInvoiceHistory: handleViewInvoiceHistory,
            onSendProofOfPaymentLink: handleSendProofOfPaymentLink,
            onCompleteEftPayment: handleCompleteEftPayment,
            onDownloadForfeitureDocument: handleDownloadForfeitureDocument,
            onDownloadOriginalCertificate: handleDownloadOriginalCertificate,
          })(row),
      }}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      onPageSizeChange={setPageSize}
      isLoading={isLoading}
      sortBy={sortBy}
      sortDescending={sortDescending}
      onSort={onSort}
    />
  );
};

export default CourseTable;
